import { useMutation } from '@tanstack/react-query';

import { useTrackGeneratedShareUrl } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { ShortUrlObjectType } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type AssetShareLinkSlotId } from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { buildShareUrlGeneratedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlGeneratedAnalyticsParams';
import { resolveShareUrlGeneratedVersion } from '~/utils/shortUrl/getShareUrlGeneratedVersion';

type GetShortIdForClipMutationParams = Parameters<typeof ShortUrl.getShortIdForClip>[0] & {
  versionSlot: AssetShareLinkSlotId;
};

export const useGetShortIdForClip = () => {
  const { trackGeneratedShareUrl } = useTrackGeneratedShareUrl();

  return useMutation({
    mutationFn: ({ versionSlot: _versionSlot, ...params }: GetShortIdForClipMutationParams) =>
      ShortUrl.getShortIdForClip(params),
    onSuccess: (data, params) => {
      trackGeneratedShareUrl(
        buildShareUrlGeneratedAnalyticsParams({
          shareId: data.id,
          shortUrl: data,
          objectType: ShortUrlObjectType.clip,
          version: resolveShareUrlGeneratedVersion({
            versionSlot: params.versionSlot,
            objectType: 'clip',
          }),
        }),
      );
    },
    onError: (error, params) => {
      throw reportErrorToBugsnag({
        error,
        context: 'Failed to get short id for clip',
        metadata: { data: { params } },
      });
    },
  });
};
